import React from 'react';
import { Bar } from 'react-chartjs-2';
import { listTransactions, getTotals } from '../../../../api/openFinanceTransactions';
import { filterTransaction } from '../../../../api/financial';
import { C, brl, fmtDate, KPICard, periodRange } from '../components/shared';
import AppContext from '../../context';

const MONTHS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function QuickBtn({ onClick, bg, border, textColor, children }) {
  return (
    <button onClick={onClick} style={{
      background: bg||C.card, color: textColor||(bg?'#fff':C.text),
      border: `1px solid ${border||bg||C.border}`,
      borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600,
      cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
    }}>{children}</button>
  );
}

function SummaryRow({ label, value, color, bold }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
      paddingBottom:8, borderBottom:`1px solid ${C.border}` }}>
      <span style={{ fontSize:12.5, color:C.soft }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:bold?800:700, color:color||C.text }}>{brl(value)}</span>
    </div>
  );
}

// Normaliza item Django para o formato comum
function _normalizeDjangoItem(t) {
  const isOverdue = t.status === 1 && new Date(t.due_date) < new Date();
  let statusStr = 'pending';
  if (t.status === 2) statusStr = 'paid';
  else if (t.status === 3) statusStr = 'canceled';
  else if (isOverdue) statusStr = 'overdue';
  return {
    _source: 'django',
    id: t.id,
    amount: (t.value || 0) + (t.tax || 0) + (t.penalty || 0),
    effective_amount: t.paid_value || ((t.value || 0) + (t.tax || 0) + (t.penalty || 0)),
    status: statusStr,
    date_due: t.due_date,
    description: t.description,
    transaction_type: t.type === 1 ? 'C' : 'D',
  };
}

export default function FinancialHome() {
  const context = React.useContext(AppContext);
  const [totals, setTotals]       = React.useState({ pay_in:{}, pay_out:{} });
  const [chartData, setChartData] = React.useState(null);
  const [upcoming, setUpcoming]   = React.useState([]);
  const [loading, setLoading]     = React.useState(true);

  async function load() {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth()+1).padStart(2,'0');
    const first = `${y}-${m}-01`;
    const last  = new Date(y, today.getMonth()+1, 0).toISOString().split('T')[0];
    const next7 = new Date(today); next7.setDate(today.getDate()+7);
    const next7S = next7.toISOString().split('T')[0];

    try {
      // ── KPI totals: open-finance + legado Django ──────────────────────────
      const [ofTot, legacyMonthRes] = await Promise.allSettled([
        getTotals({ start_date: first, end_date: last }),
        filterTransaction({ end_count: 1000, start_date: first, end_date: last }),
      ]);

      const ofTotals = (ofTot.status === 'fulfilled' && ofTot.value) ? ofTot.value : { pay_in:{}, pay_out:{} };
      const pi = { ...(ofTotals.pay_in  || {}) };
      const po = { ...(ofTotals.pay_out || {}) };

      // Soma os dados do Django legado aos totais do OF
      if (legacyMonthRes.status === 'fulfilled' && legacyMonthRes.value) {
        const rawLegacy = Array.isArray(legacyMonthRes.value)
          ? legacyMonthRes.value
          : (legacyMonthRes.value.results || legacyMonthRes.value.data || []);
        rawLegacy.forEach(t => {
          const n = _normalizeDjangoItem(t);
          if (n.transaction_type === 'C') {
            if (n.status === 'paid')    pi.received_liquid = (pi.received_liquid||0) + n.effective_amount;
            if (n.status === 'pending') pi.to_receive      = (pi.to_receive||0)      + n.amount;
            if (n.status === 'overdue') pi.inadimplente    = (pi.inadimplente||0)    + n.amount;
          } else {
            if (n.status === 'paid')    po.paid_liquid     = (po.paid_liquid||0)     + n.effective_amount;
            if (n.status === 'pending') po.to_pay          = (po.to_pay||0)          + n.amount;
            if (n.status === 'overdue') po.overdue         = (po.overdue||0)         + n.amount;
          }
        });
      }

      setTotals({ pay_in: pi, pay_out: po });

      // ── Gráfico mensal: 6 meses (OF + legado) ─────────────────────────────
      const monthlyPromises = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(y, today.getMonth() - 5 + i, 1);
        const s = d.toISOString().split('T')[0];
        const e = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().split('T')[0];
        const label = MONTHS[d.getMonth()];
        return Promise.allSettled([
          getTotals({ start_date: s, end_date: e }),
          filterTransaction({ end_count: 1000, start_date: s, end_date: e }),
        ]).then(([ofR, legR]) => {
          let received = (ofR.value && ofR.value.pay_in && ofR.value.pay_in.received_liquid) || 0;
          let paid     = (ofR.value && ofR.value.pay_out && ofR.value.pay_out.paid_liquid)   || 0;
          if (legR.status === 'fulfilled' && legR.value) {
            const raw = Array.isArray(legR.value) ? legR.value : (legR.value.results || legR.value.data || []);
            raw.forEach(t => {
              const n = _normalizeDjangoItem(t);
              if (n.status === 'paid') {
                if (n.transaction_type === 'C') received += n.effective_amount;
                else                            paid     += n.effective_amount;
              }
            });
          }
          return { label, received, paid };
        });
      });
      const monthly = await Promise.all(monthlyPromises);
      setChartData({
        labels: monthly.map(m => m.label),
        datasets: [
          { label:'Recebido', data: monthly.map(m => m.received), backgroundColor: C.green, borderRadius:4 },
          { label:'Pago',     data: monthly.map(m => m.paid),     backgroundColor: C.red,   borderRadius:4 },
        ],
      });

      // ── Próximos vencimentos: OF + legado ─────────────────────────────────
      const [pIn, pOut, ovIn, ovOut, legUpcoming] = await Promise.allSettled([
        listTransactions({ transaction_type:'C', status:'pending', end_date: next7S, per_page:20 }),
        listTransactions({ transaction_type:'D', status:'pending', end_date: next7S, per_page:20 }),
        listTransactions({ transaction_type:'C', status:'overdue', per_page:10 }),
        listTransactions({ transaction_type:'D', status:'overdue', per_page:10 }),
        filterTransaction({ end_count: 50, status: 1, end_date: next7S }), // status=1 = pending no Django
      ]);

      const ofUpcoming = [
        ...((pIn.value  && pIn.value.transactions)  || []).map(t=>({...t, tipo:'receita'})),
        ...((pOut.value && pOut.value.transactions) || []).map(t=>({...t, tipo:'despesa'})),
        ...((ovIn.value && ovIn.value.transactions) || []).map(t=>({...t, tipo:'receita'})),
        ...((ovOut.value && ovOut.value.transactions) || []).map(t=>({...t, tipo:'despesa'})),
      ];

      let djangoUpcoming = [];
      if (legUpcoming.status === 'fulfilled' && legUpcoming.value) {
        const raw = Array.isArray(legUpcoming.value) ? legUpcoming.value : (legUpcoming.value.results || legUpcoming.value.data || []);
        djangoUpcoming = raw.map(t => ({
          ..._normalizeDjangoItem(t),
          tipo: t.type === 1 ? 'receita' : 'despesa',
        }));
      }

      const combined = [...ofUpcoming, ...djangoUpcoming]
        .sort((a,b) => new Date(a.date_due) - new Date(b.date_due))
        .slice(0, 8);
      setUpcoming(combined);

    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }


  React.useEffect(() => { load(); }, [context.currentBranch]);

  const pi = totals.pay_in  || {};
  const po = totals.pay_out || {};
  const resultado = (pi.received_liquid||0) - (po.paid_liquid||0);

  function daysInfo(ds) {
    if (!ds) return { label:'—', color:C.soft };
    const t = new Date(); t.setHours(0,0,0,0);
    const d = new Date(ds); d.setHours(0,0,0,0);
    const diff = Math.round((d-t)/86400000);
    if (diff < 0)   return { label:`${Math.abs(diff)}d atrasado`, color:C.red };
    if (diff === 0) return { label:'Vence hoje', color:'#D68910' };
    return             { label:`em ${diff}d`,  color:C.blue };
  }

  const chartOpts = {
    responsive:true,
    plugins:{ legend:{ position:'top', labels:{ font:{size:11}, boxWidth:12 } }, tooltip:{ callbacks:{ label: ctx => brl(ctx.raw) } } },
    scales:{
      x:{ grid:{display:false}, ticks:{color:C.soft, font:{size:11}} },
      y:{ grid:{color:'#F1F5F9'}, ticks:{color:C.soft, font:{size:11}, callback:v=>`R$${(v/1000).toFixed(0)}k`} },
    },
  };

  return (
    <div style={{ paddingBottom:32 }}>
      {/* KPI Row */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <KPICard icon="arrow-down" label="Recebido no mês" value={brl(pi.received_liquid)} valueColor={C.green}
          sub={<React.Fragment>+ <strong style={{color:C.text}}>{brl(pi.to_receive)}</strong> a receber</React.Fragment>}
          iconBg={C.greenBg} iconColor={C.green}/>
        <KPICard icon="arrow-up" label="Pago no mês" value={brl(po.paid_liquid)} valueColor={C.red}
          sub={<React.Fragment>+ <strong style={{color:C.text}}>{brl(po.to_pay)}</strong> a pagar</React.Fragment>}
          iconBg={C.redBg} iconColor={C.red}/>
        <KPICard icon="balance-scale" label="Resultado realizado"
          value={(resultado>=0?'+ ':'− ')+brl(Math.abs(resultado))}
          valueColor={resultado>=0?C.green:C.red}
          sub={<React.Fragment>Projetado: <strong style={{color:C.text}}>{brl((pi.received_liquid||0)+(pi.to_receive||0)-(po.paid_liquid||0)-(po.to_pay||0))}</strong></React.Fragment>}
          iconBg={C.blueBg} iconColor={C.blue}/>
        <KPICard icon="clock" label="Pendente total"
          value={brl((pi.to_receive||0)+(po.to_pay||0))} valueColor={C.amber}
          sub={<React.Fragment>A receber {brl(pi.to_receive)} · A pagar {brl(po.to_pay)}</React.Fragment>}
          iconBg={C.amberBg} iconColor={C.amber}/>
      </div>

      {/* Quick Actions */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        <QuickBtn bg={C.green} onClick={()=>window.location.href='/financial/pay-in'}>
          <i className="fas fa-plus" style={{fontSize:11}}/> Nova conta a receber
        </QuickBtn>
        <QuickBtn bg={C.red} onClick={()=>window.location.href='/financial/pay-out'}>
          <i className="fas fa-plus" style={{fontSize:11}}/> Nova conta a pagar
        </QuickBtn>
        <QuickBtn border={C.border} onClick={()=>window.location.href='/financial/pay-out'}>
          <i className="fas fa-money-check-alt" style={{fontSize:11}}/> Contas a Pagar
        </QuickBtn>
        <QuickBtn border={C.border} onClick={()=>window.location.href='/financial/pay-in'}>
          <i className="fas fa-hand-holding-usd" style={{fontSize:11}}/> Contas a Receber
        </QuickBtn>
      </div>

      {/* Chart + Upcoming */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Receitas vs Despesas</div>
            <div style={{ fontSize:11.5, color:C.soft }}>Últimos 6 meses · valores realizados</div>
          </div>
          {loading
            ? <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.soft }}><i className="fas fa-spinner fa-spin"/> &nbsp;Carregando...</div>
            : chartData
              ? <Bar data={chartData} options={chartOpts} height={200}/>
              : <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.soft }}>Sem dados</div>
          }
        </div>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Próximos vencimentos</div>
              <div style={{ fontSize:11.5, color:C.soft }}>7 dias + atrasados</div>
            </div>
            {upcoming.length > 0 && (
              <span style={{ background:C.amberBg, color:C.amber, fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
                {upcoming.length}
              </span>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {upcoming.length === 0 && !loading && (
              <p style={{ color:C.soft, fontSize:13 }}>Nenhum vencimento próximo 🎉</p>
            )}
            {upcoming.map((t, i) => {
              const info = daysInfo(t.date_due && t.date_due.slice(0,10));
              return (
                <div key={t.id||i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, background: i%2===0?'#F8FAFC':'transparent' }}>
                  <div style={{ width:34, height:34, borderRadius:8, flexShrink:0,
                    background: t.tipo==='receita'?C.greenBg:C.redBg,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className={`fas fa-arrow-${t.tipo==='receita'?'down':'up'}`}
                       style={{ color:t.tipo==='receita'?C.green:C.red, fontSize:11 }}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12.5, fontWeight:600, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {t.description}
                    </div>
                    <div style={{ fontSize:10.5, color:info.color, fontWeight:700 }}>{info.label}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:800, color: t.tipo==='receita'?C.green:C.red, flexShrink:0 }}>
                    {brl(t.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <span style={{ fontSize:15, fontWeight:700, color:C.text }}>Contas a Receber</span>
            <button onClick={()=>window.location.href='/financial/pay-in'} style={{ background:'none', border:'none', color:C.blue, fontSize:12, fontWeight:600, cursor:'pointer' }}>Ver todas →</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <SummaryRow label="Recebido no mês" value={pi.received_liquid||0} color={C.green}/>
            <SummaryRow label="A receber (pendente)" value={pi.to_receive||0} color={C.amber}/>
            <SummaryRow label="Inadimplente" value={pi.inadimplente||0} color={C.red}/>
            <SummaryRow label="Total previsto" value={(pi.received_liquid||0)+(pi.to_receive||0)} color={C.text} bold/>
          </div>
        </div>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <span style={{ fontSize:15, fontWeight:700, color:C.text }}>Contas a Pagar</span>
            <button onClick={()=>window.location.href='/financial/pay-out'} style={{ background:'none', border:'none', color:C.blue, fontSize:12, fontWeight:600, cursor:'pointer' }}>Ver todas →</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <SummaryRow label="Pago no mês" value={po.paid_liquid||0} color={C.red}/>
            <SummaryRow label="A pagar (pendente)" value={po.to_pay||0} color={C.amber}/>
            <SummaryRow label="Vencido" value={po.overdue||0} color={C.red}/>
            <SummaryRow label="Total comprometido" value={(po.paid_liquid||0)+(po.to_pay||0)} color={C.text} bold/>
          </div>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/Home/index.jsx